import { cva } from 'class-variance-authority'

// Shapes and fills follow the orderx.com style guide (STYLE_GUIDE.md): rounded-md buttons
// (shape is applied in button.tsx; `shape="pill"` for marketing CTAs),
// solid foreground primary, cyan accent, translucent-foreground secondary. Colors use
// foreground/background tokens so the same variant reads correctly in both themes.
export const buttonVariants = cva(
  // Every button settles a touch under the pointer while pressed (motion-safe only), so a
  // click reads as physical feedback app-wide without each call site animating it.
  'inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap text-sm font-medium transition-[color,background-color,border-color,opacity,scale] duration-150 ease-out motion-safe:not-disabled:active:scale-[0.97] disabled:pointer-events-none disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-emphasis text-emphasis-foreground not-disabled:hover:bg-emphasis/85',
        // Emphasis CTA for secondary prompts (empty states, dialogs, tutorial steps): the
        // --emphasis fill — translucent white in the dark/blue themes, solid in light. Real
        // submits ("Submit Order", "Start Hedge", Risk Slides "Run") use `basic` instead (via
        // SubmitActionButton). Call sites must not set their own text color.
        cta: 'bg-emphasis text-emphasis-foreground font-medium disabled:bg-foreground/10 disabled:text-foreground/40 disabled:opacity-100 not-disabled:hover:bg-emphasis/85 not-disabled:active:bg-emphasis/70',
        // The one cyan accent: submit actions (SubmitActionButton) and the "Get Pro" button.
        basic: 'bg-ox-cyan text-ox-ink not-disabled:hover:bg-ox-cyan/85 min-w-32',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-foreground/15 bg-transparent text-foreground not-disabled:hover:bg-foreground/10',
        outlineSubtle:
          'border border-foreground/10 bg-transparent text-foreground/60 not-disabled:hover:border-foreground/20 not-disabled:hover:bg-foreground/5 not-disabled:hover:text-foreground',
        secondary: 'bg-foreground/10 text-foreground not-disabled:hover:bg-foreground/15',
        ghost: 'not-disabled:hover:bg-foreground/5 hover:text-foreground',
        link: 'text-primary underline-offset-2 hover:underline',
        actionSuccess:
          'font-medium bg-chart-mint/10 text-chart-mint border border-chart-mint/50 not-disabled:hover:bg-chart-mint not-disabled:hover:text-ox-ink py-2 px-4 flex items-center gap-2 text-center disabled:opacity-30 disabled:cursor-not-allowed disabled:pointer-events-none',
        actionDestructive:
          'font-medium bg-chart-red/10 text-chart-red border border-chart-red/50 not-disabled:hover:bg-chart-red not-disabled:hover:text-ox-ink py-2 px-4 flex items-center gap-2 text-center disabled:opacity-30 disabled:cursor-not-allowed disabled:pointer-events-none',
        // Quiet text button for toolbars and secondary actions: dim foreground that
        // brightens on hover, no fill at rest (plain `ghost` inherits the text color).
        navigation:
          'text-foreground/60 not-disabled:hover:bg-foreground/5 not-disabled:hover:text-foreground data-[state=open]:text-foreground data-[current=true]:text-foreground data-[current=true]:font-medium',
        subtle:
          'text-foreground/50 not-disabled:hover:bg-foreground/5 not-disabled:hover:text-foreground',
        // `subtle` whose hover confirms (check) or warns (delete/cancel).
        subtleSuccess:
          'text-foreground/50 not-disabled:hover:bg-foreground/5 not-disabled:hover:text-chart-mint',
        subtleDestructive:
          'text-foreground/50 not-disabled:hover:bg-chart-red/10 not-disabled:hover:text-chart-red',
        // Bordered quiet destructive — a secondary "cancel" under a pair of solid actions.
        outlineDestructive:
          'border border-foreground/10 bg-foreground/5 text-foreground/55 not-disabled:hover:border-chart-red/30 not-disabled:hover:bg-chart-red/10 not-disabled:hover:text-chart-red disabled:text-foreground/25 disabled:opacity-100',
        // Solid trading-side fills (RFQ accept Buy/Sell): the loudest buy/sell action, where
        // the tinted actionSuccess/actionDestructive would undersell a one-click execution.
        solidSuccess:
          'bg-chart-mint text-ox-ink not-disabled:hover:bg-chart-mint/90 disabled:bg-chart-mint/10 disabled:text-chart-mint/40 disabled:opacity-100',
        solidDestructive:
          'bg-chart-red text-ox-paper not-disabled:hover:bg-chart-red/90 disabled:bg-chart-red/10 disabled:text-chart-red/40 disabled:opacity-100',
        actionNeutral:
          'font-medium text-foreground bg-transparent border border-foreground/15 not-disabled:hover:bg-foreground/10 py-2 px-4 flex items-center gap-2 text-center disabled:opacity-30 disabled:cursor-not-allowed disabled:pointer-events-none',
      },
      size: {
        default: 'h-10 px-5',
        sm: 'h-8 px-4',
        // Dense toolbar button (widget headers, date pagers).
        xs: 'h-6 gap-0.5 px-2 text-xs',
        // Square icon-only button at the dense-toolbar height (clear ×, pager arrows).
        iconXs: 'size-6 p-0 text-xs [&_svg]:size-3.5',
        lg: 'h-11 px-8 text-base',
        icon: 'bg-transparent p-1 not-disabled:hover:bg-foreground/10',
        // Top-bar tool (Figma "Toolbar button"): a 32px round hit area around a 16px glyph,
        // foreground/60 at rest. Round like ghost icon buttons (see button.tsx).
        toolbar:
          'size-8 p-0 text-foreground/60 not-disabled:hover:bg-foreground/5 not-disabled:hover:text-foreground motion-safe:not-disabled:active:scale-[0.94] [&_svg]:size-4',
        action: 'h-[33px] text-sm',
      },
    },
    compoundVariants: [
      {
        variant: 'ghost',
        size: 'icon',
        class: 'p-2',
      },
      // `icon` size is transparent, so the fill-contrast text colors of `default`/`basic`
      // would vanish against the page — every variant but `destructive` gets dim foreground.
      {
        variant: ['default', 'basic', 'outline', 'secondary', 'ghost', 'link'],
        size: 'icon',
        class: 'text-foreground/80 min-w-0',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)
